import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getImageUrl } from '../../utils/api';
import AssetPhotoUpload from '../../components/assets/AssetPhotoUpload';
import InputModal from '../../components/modals/InputModal';
import Loader from '../../components/common/Loader';
import { useAssetForm } from '../../contexts/AssetFormContext';
import { PERMISSIONS, canFeatureForRole } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import '../../styles/Forms.css';

const EditAssetPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmployeeArea = location.pathname.startsWith('/employee');
  const assetsHomePath = isEmployeeArea ? '/employee/assets' : '/admin/assets';
  const { id } = useParams();
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(u?.role || '').trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }, []);
  const canEditAssets = useMemo(
    () => canFeatureForRole(normalizedUserRole, rolePermissions, [PERMISSIONS.EDIT_ASSETS]),
    [normalizedUserRole, rolePermissions]
  );

  useEffect(() => {
    if (!permissionsReady) return;
    if (!canEditAssets) {
      toast.error('You do not have permission to edit assets');
      navigate(assetsHomePath, { replace: true });
    }
  }, [permissionsReady, canEditAssets, navigate, assetsHomePath]);
  const {
    getEditFormDataForAsset,
    getEditPhotoPreviewForAsset,
    setEditFormDataForAsset,
    setEditFormFieldForAsset,
    setEditPhotoPreviewForAsset,
    clearEditFormDataForAsset,
  } = useAssetForm();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [inputModal, setInputModal] = useState({ isOpen: false, type: null });
  const [fieldErrors, setFieldErrors] = useState({});

  // Get form data from context or use empty state
  const savedFormData = getEditFormDataForAsset(id);
  const savedPhotoPreview = getEditPhotoPreviewForAsset(id);
  
  const [formData, setFormData] = useState(savedFormData || {
    description: '',
    assetTag: '',
    purchaseDate: '',
    purchasedFrom: '',
    brand: '',
    model: '',
    serialNumber: '',
    cost: '',
    site: '',
    location: '',
    category: '',
    department: '',
  });
  
  const [photoPreviews, setPhotoPreviews] = useState([]);

  useEffect(() => {
    fetchInitialData();
    fetchAssetData();
  }, [id]);

  // Refresh data when component becomes visible (user returns from navigation)
  useEffect(() => {
    const handleFocus = () => {
      fetchInitialData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      photoPreviews.forEach(preview => {
        if (preview.preview && preview.preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview.preview);
        }
      });
    };
  }, [photoPreviews]);

  const fetchInitialData = async () => {
    try {
      // Fetch sites
      const sitesRes = await api.getSites({ isActive: 'true' });
      if (sitesRes.success) {
        setSites(sitesRes.data || []);
      }

      // Fetch departments
      const deptsRes = await api.getDepartments({ isActive: 'true' });
      if (deptsRes.success) {
        setDepartments(deptsRes.data || []);
      }

      // Fetch categories
      const categoriesRes = await api.getCategories({ isActive: 'true' });
      if (categoriesRes.success) {
        setCategories(categoriesRes.data || []);
      }

      // Fetch locations
      const locationsRes = await api.getLocations({ isActive: 'true' });
      if (locationsRes.success) {
        setLocations(locationsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchAssetData = async () => {
    try {
      setLoading(true);
      const response = await api.getAsset(id);
      if (response.success) {
        const asset = response.data;
        
        // Check if we have saved form data in context (user was editing and navigated away)
        const savedFormData = getEditFormDataForAsset(id);
        
        if (savedFormData && savedFormData.description) {
          // We have saved data, restore user's changes
          setFormData(savedFormData);
          const savedPhotoPreview = getEditPhotoPreviewForAsset(id);
          if (savedPhotoPreview) {
            setPhotoPreviews(Array.isArray(savedPhotoPreview) ? savedPhotoPreview : [savedPhotoPreview]);
          } else {
            // Load existing photos from asset
            const existing = (asset.photos || []).map(photo => ({
              id: photo._id || photo.id,
              url: photo.url,
              preview: getImageUrl(photo.url),
              isExisting: true
            }));
            setExistingPhotos(existing);
            setPhotoPreviews(existing);
          }
        } else {
          // No saved data, use API data
          const apiFormData = {
            description: asset.description || '',
            assetTag: asset.assetTag || '',
            purchaseDate: asset.purchaseDate ? new Date(asset.purchaseDate).toISOString().split('T')[0] : '',
            purchasedFrom: asset.purchasedFrom || '',
            brand: asset.brand || '',
            model: asset.model || '',
            serialNumber: asset.serialNumber || '',
            cost: asset.cost || '',
            site: asset.location?.site?._id || asset.location?.site?.id || asset.location?.site || '',
            location: asset.location?.locationDetail || '',
            category: asset.category || '',
            department: asset.location?.department?._id || asset.location?.department?.id || asset.location?.department || '',
          };
          setFormData(apiFormData);
          setEditFormDataForAsset(id, apiFormData);
          
          // Load all existing photos
          const existing = (asset.photos || []).map(photo => ({
            id: photo._id || photo.id,
            url: photo.url,
            preview: getImageUrl(photo.url),
            isExisting: true
          }));
          setExistingPhotos(existing);
          setPhotoPreviews(existing);
        }
      } else {
        toast.error('Error loading asset data');
        navigate(assetsHomePath);
      }
    } catch (error) {
      console.error('Error fetching asset:', error);
      toast.error('Error loading asset data');
      navigate(assetsHomePath);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Clear error for this field when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Save to context immediately
      if (id) {
        setEditFormFieldForAsset(id, name, value);
      }
      return updated;
    });
  };

  const handlePhotoChange = (filesOrUpdatedList) => {
    if (Array.isArray(filesOrUpdatedList)) {
      // Check if it's an array of File objects (new upload) or preview objects (removal)
      const isFileObjects = filesOrUpdatedList.length > 0 && filesOrUpdatedList[0] instanceof File;
      
      if (isFileObjects) {
        // New files uploaded - create preview objects
        const newPreviews = filesOrUpdatedList.map(file => ({
          file,
          preview: URL.createObjectURL(file),
          isExisting: false
        }));
        // Combine with existing photos
        const combined = [...photoPreviews, ...newPreviews];
        setPhotoFiles(combined.filter(p => p.file).map(p => p.file));
        setPhotoPreviews(combined);
        // Save to context
        if (id) {
          setEditPhotoPreviewForAsset(id, combined);
        }
      } else {
        // Updated list after removal - check if we removed an existing photo
        const removedPhotos = photoPreviews.filter(
          oldPreview => !filesOrUpdatedList.find(newPreview => 
            (oldPreview.id && newPreview.id && oldPreview.id === newPreview.id) ||
            (oldPreview.preview === newPreview.preview)
          )
        );
        
        // Mark existing photos for deletion
        removedPhotos.forEach(photo => {
          if (photo.isExisting && photo.id && !photosToDelete.includes(photo.id)) {
            setPhotosToDelete(prev => [...prev, photo.id]);
          }
          // Clean up object URLs
          if (photo.preview && photo.preview.startsWith('blob:')) {
            URL.revokeObjectURL(photo.preview);
          }
        });
        
        setPhotoFiles(filesOrUpdatedList.filter(p => p.file).map(p => p.file));
        setPhotoPreviews(filesOrUpdatedList);
        // Save to context
        if (id) {
          setEditPhotoPreviewForAsset(id, filesOrUpdatedList);
        }
      }
    }
  };

  const handleAddNew = async (type, value) => {
    try {
      let response;
      switch (type) {
        case 'site':
          response = await api.createSite({ name: value, isActive: true });
          if (response.success) {
            setSites([...sites, response.data]);
            setFormData(prev => {
              const updated = { ...prev, site: response.data.id || response.data._id };
              if (id) setEditFormFieldForAsset(id, 'site', updated.site);
              return updated;
            });
            toast.success('Site added successfully');
          }
          break;
        case 'location':
          response = await api.createLocation({ name: value, isActive: true });
          if (response.success) {
            setLocations([...locations, response.data]);
            setFormData(prev => {
              const updated = { ...prev, location: response.data.name };
              if (id) setEditFormFieldForAsset(id, 'location', updated.location);
              return updated;
            });
            toast.success('Location created successfully');
          }
          break;
        case 'category':
          response = await api.createCategory({ name: value, isActive: true });
          if (response.success) {
            setCategories([...categories, response.data]);
            setFormData(prev => {
              const updated = { ...prev, category: response.data.name };
              if (id) setEditFormFieldForAsset(id, 'category', updated.category);
              return updated;
            });
            toast.success('Category created successfully');
          }
          break;
        case 'department':
          response = await api.createDepartment({ name: value, isActive: true });
          if (response.success) {
            setDepartments([...departments, response.data]);
            setFormData(prev => {
              const updated = { ...prev, department: response.data.id || response.data._id };
              if (id) setEditFormFieldForAsset(id, 'department', updated.department);
              return updated;
            });
            toast.success('Department added successfully');
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error adding new ${type}:`, error);
      toast.error(`Error adding new ${type}. Please try again.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setFieldErrors({});

    // Client-side validation
    const errors = {};
    if (!formData.description) {
      errors.description = 'Description is required';
    }
    if (!formData.assetTag) {
      errors.assetTag = 'Asset Tag ID is required';
    }
    if (!formData.category) {
      errors.category = 'Category is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      setSaving(true);

      // Prepare asset data
      const assetData = {
        description: formData.description,
        assetTag: formData.assetTag.toUpperCase(),
        category: formData.category,
        brand: formData.brand,
        model: formData.model,
        serialNumber: formData.serialNumber,
        purchasedFrom: formData.purchasedFrom,
        cost: formData.cost ? parseFloat(formData.cost) : 0,
        purchaseDate: formData.purchaseDate || undefined,
        location: {
          site: formData.site || undefined,
          department: formData.department || undefined,
          locationDetail: formData.location || undefined,
        },
      };

      // Update asset
      const response = await api.updateAsset(id, assetData);

      if (response.success) {
        // Delete photos that were removed
        if (photosToDelete.length > 0) {
          try {
            await Promise.all(
              photosToDelete.map(photoId => api.deleteAssetPhoto(id, photoId))
            );
          } catch (error) {
            console.error('Error deleting photos:', error);
            toast.error('Some photos could not be deleted');
          }
        }

        // Upload new photos
        if (photoFiles.length > 0) {
          try {
            const uploadResponse = await api.uploadAssetPhoto(id, photoFiles);
            if (uploadResponse.success) {
              toast.success(`Asset updated with ${uploadResponse.count || photoFiles.length} new photo(s)!`);
            }
          } catch (error) {
            console.error('Error uploading photos:', error);
            toast.error('Asset updated but photo upload failed');
          }
        } else if (photosToDelete.length > 0) {
          toast.success('Asset updated successfully!');
        } else {
          toast.success('Asset updated successfully!');
        }
      }

      // Clear form data from context after successful submission
      clearEditFormDataForAsset(id);
      // Clean up object URLs
      photoPreviews.forEach(preview => {
        if (preview.preview && preview.preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview.preview);
        }
      });
      setFieldErrors({});
      navigate(assetsHomePath);
    } catch (error) {
      console.error('Error updating asset:', error);
      
      // Parse error message and map to specific fields
      const errorMessage = error.message || 'Unknown error';
      const newErrors = {};

      // Check for assetTag duplicate error
      if (errorMessage.toLowerCase().includes('asset tag') || 
          errorMessage.toLowerCase().includes('already exists') ||
          errorMessage.toLowerCase().includes('already in used')) {
        newErrors.assetTag = 'Asset Tag ID is already in used';
      } else if (errorMessage.toLowerCase().includes('description')) {
        newErrors.description = errorMessage;
      } else if (errorMessage.toLowerCase().includes('category')) {
        newErrors.category = errorMessage;
      } else {
        // For other errors, show as general error
        newErrors._general = errorMessage;
      }

      setFieldErrors(newErrors);
      
      // Only show toast for general errors, not field-specific ones
      if (newErrors._general) {
        toast.error('Error updating asset: ' + errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!permissionsReady) {
    return <Loader />;
  }
  if (!canEditAssets) {
    return null;
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="edit-asset-page" style={{ padding: '16px' }}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          {/* Left Column - Basic Information */}
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Basic Information</h2>
            
            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <input
                id="description"
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter asset description"
                required
                style={{
                  borderColor: fieldErrors.description ? '#e53e3e' : undefined
                }}
              />
              {fieldErrors.description && (
                <div style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                  {fieldErrors.description}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="assetTag">Asset Tag ID *</label>
              <input
                id="assetTag"
                type="text"
                name="assetTag"
                value={formData.assetTag}
                onChange={handleInputChange}
                placeholder="Enter asset tag ID"
                required
                style={{
                  textTransform: 'uppercase',
                  borderColor: fieldErrors.assetTag ? '#e53e3e' : undefined
                }}
              />
              {fieldErrors.assetTag && (
                <div style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                  {fieldErrors.assetTag}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  style={{
                    flex: 1,
                    borderColor: fieldErrors.category ? '#e53e3e' : undefined
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id || cat._id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                  {!isEmployeeArea && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/categories/add')}
                    style={{
                    padding: '8px 12px',
                    background: '#f7fafc',
                    border: '2px solid #e2e8f0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}
                >
                  + Add
                </button>
                  )}
              </div>
              {fieldErrors.category && (
                <div style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                  {fieldErrors.category}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="brand">Brand</label>
              <input
                id="brand"
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleInputChange}
                placeholder="Enter brand name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="model">Model</label>
              <input
                id="model"
                type="text"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                placeholder="Enter model number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="serialNumber">Serial Number</label>
              <input
                id="serialNumber"
                type="text"
                name="serialNumber"
                value={formData.serialNumber}
                onChange={handleInputChange}
                placeholder="Enter serial number"
              />
            </div>

            <div className="form-group">
              <label htmlFor="purchasedFrom">Purchased From</label>
              <input
                id="purchasedFrom"
                type="text"
                name="purchasedFrom"
                value={formData.purchasedFrom}
                onChange={handleInputChange}
                placeholder="Enter vendor/supplier name"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label htmlFor="cost">Cost (₹)</label>
                <input
                  id="cost"
                  type="number"
                  name="cost"
                  value={formData.cost}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="purchaseDate">Purchase Date</label>
                <input
                  id="purchaseDate"
                  type="date"
                  name="purchaseDate"
                  value={formData.purchaseDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Location & Photo */}
          <div>
            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '16px'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Location & Department</h2>
              
              <div className="form-group">
                <label htmlFor="site">Site</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    id="site"
                    name="site"
                    value={formData.site}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select Site</option>
                    {sites.map(site => (
                      <option key={site.id || site._id} value={site.id || site._id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                  {!isEmployeeArea && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/sites/add')}
                    style={{
                      padding: '8px 12px',
                      background: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + Add
                  </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select Location</option>
                    {locations.map(loc => (
                      <option key={loc.id || loc._id} value={loc.name}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                  {!isEmployeeArea && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/locations/add')}
                    style={{
                      padding: '8px 12px',
                      background: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + Add
                  </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="department">Department</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id || dept._id} value={dept.id || dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  {!isEmployeeArea && (
                  <button
                    type="button"
                    onClick={() => navigate('/admin/departments/add')}
                    style={{
                      padding: '8px 12px',
                      background: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    + Add
                  </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Asset Photos</h2>
              <AssetPhotoUpload
                onUpload={handlePhotoChange}
                currentPhotos={photoPreviews}
              />
              <small style={{ color: '#718096', fontSize: '11px', marginTop: '8px', display: 'block' }}>
                Allowed formats: JPG, GIF, PNG. Maximum 25MB per file. You can add multiple photos and remove existing ones.
              </small>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
          paddingTop: '16px',
          borderTop: '1px solid #e2e8f0'
        }}>
          <button
            type="button"
            onClick={() => navigate(assetsHomePath)}
            style={{
              padding: '10px 20px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Updating...' : 'Update Asset'}
          </button>
        </div>
      </form>

      {/* Input Modals */}
      <InputModal
        isOpen={inputModal.isOpen && inputModal.type === 'site'}
        onClose={() => setInputModal({ isOpen: false, type: null })}
        onSubmit={(value) => handleAddNew('site', value)}
        title="Add New Site"
        label="Site Name"
        placeholder="Enter new site name"
      />

      <InputModal
        isOpen={inputModal.isOpen && inputModal.type === 'location'}
        onClose={() => setInputModal({ isOpen: false, type: null })}
        onSubmit={(value) => handleAddNew('location', value)}
        title="Add New Location"
        label="Location Name"
        placeholder="Enter new location name"
      />

      <InputModal
        isOpen={inputModal.isOpen && inputModal.type === 'category'}
        onClose={() => setInputModal({ isOpen: false, type: null })}
        onSubmit={(value) => handleAddNew('category', value)}
        title="Add New Category"
        label="Category Name"
        placeholder="Enter new category name"
      />

      <InputModal
        isOpen={inputModal.isOpen && inputModal.type === 'department'}
        onClose={() => setInputModal({ isOpen: false, type: null })}
        onSubmit={(value) => handleAddNew('department', value)}
        title="Add New Department"
        label="Department Name"
        placeholder="Enter new department name"
      />

      {saving && <Loader />}
    </div>
  );
};

export default EditAssetPage;

